export type ExperienceQuality = 'high' | 'balanced' | 'lite'
export type ExperiencePreference = ExperienceQuality | 'auto'
export type IntroMode = 'full' | 'compact' | 'instant'

export interface ExperienceSignals {
  preference: ExperiencePreference
  reducedMotion: boolean
  viewportWidth: number
  deviceMemory?: number
  hardwareConcurrency?: number
}

export interface IntroSignals {
  reducedMotion: boolean
  visitedThisSession: boolean
}

export interface PointerPosition {
  x: number
  y: number
}

export interface RenderSignals {
  documentVisible: boolean
  sceneVisible: boolean
  quality: ExperienceQuality
}

export type RenderCadence = 'active' | 'throttled' | 'paused'

export function resolveExperienceQuality(signals: ExperienceSignals): ExperienceQuality {
  if (signals.reducedMotion) {
    return 'lite'
  }

  if (signals.preference !== 'auto') {
    return signals.preference
  }

  if (signals.viewportWidth < 640) {
    return 'lite'
  }

  const constrainedMemory = typeof signals.deviceMemory === 'number' && signals.deviceMemory <= 4
  const constrainedCpu =
    typeof signals.hardwareConcurrency === 'number' && signals.hardwareConcurrency <= 4

  if (constrainedMemory || constrainedCpu || signals.viewportWidth < 1100) {
    return 'balanced'
  }

  return 'high'
}

export function getIntroMode(signals: IntroSignals): IntroMode {
  if (signals.reducedMotion) {
    return 'instant'
  }

  return signals.visitedThisSession ? 'compact' : 'full'
}

export function getParticleBudget(quality: ExperienceQuality, isMobile: boolean): number {
  const desktopBudgets: Record<ExperienceQuality, number> = {
    high: 1600,
    balanced: 900,
    lite: 320,
  }
  const mobileBudgets: Record<ExperienceQuality, number> = {
    high: 620,
    balanced: 360,
    lite: 180,
  }

  return (isMobile ? mobileBudgets : desktopBudgets)[quality]
}

export function normalizePointer(
  clientX: number,
  clientY: number,
  viewportWidth: number,
  viewportHeight: number,
): PointerPosition {
  const safeWidth = Math.max(viewportWidth, 1)
  const safeHeight = Math.max(viewportHeight, 1)
  const clamp = (value: number) => Math.max(-1, Math.min(1, value))

  return {
    x: clamp((clientX / safeWidth) * 2 - 1),
    y: clamp(1 - (clientY / safeHeight) * 2),
  }
}

export function getRenderCadence(signals: RenderSignals): RenderCadence {
  if (!signals.documentVisible || !signals.sceneVisible) {
    return 'paused'
  }

  return signals.quality === 'lite' ? 'throttled' : 'active'
}
